import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { User } from "@/hooks/use-users";
import { useDeactivateUser, useReactivateUser } from "@/hooks/use-users";

interface DeactivateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  mode: "deactivate" | "reactivate";
}

export function DeactivateUserDialog({
  open,
  onOpenChange,
  user,
  mode,
}: DeactivateUserDialogProps) {
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();

  const isPending = mode === "deactivate" ? deactivate.isPending : reactivate.isPending;

  function handleConfirm() {
    if (mode === "deactivate") {
      deactivate.mutate(user.id, { onSuccess: () => onOpenChange(false) });
    } else {
      reactivate.mutate(user.id, { onSuccess: () => onOpenChange(false) });
    }
  }

  if (mode === "reactivate") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reactivate Account</DialogTitle>
            <DialogDescription>
              Reactivating <strong>{user.name}</strong> will restore their ability to log in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isPending}>
              {isPending ? "Reactivating..." : "Reactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Deactivate Account</DialogTitle>
          <DialogDescription>
            Deactivating <strong>{user.name}</strong> will prevent them from logging in. Their
            historical data will be preserved. Confirm?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isPending}>
            {isPending ? "Deactivating..." : "Deactivate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
